import type { EpocusEncounter } from "$global-providers/rxdb-provider/collections/epocus-encounter";
import type { ImagingInstance } from "$global-providers/rxdb-provider/collections/imaging-instance";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useUser } from "$global-providers/user-provider/provider";
import { useEffect } from "react";
import type { RxCollection } from "rxdb";
import { matchDeviceFromInstances } from "../../../_lib/device-dicom-matching";

/**
 * PNE-427: scan data is authoritative for the encounter device. When imaging
 * instances attach to an encounter and carry a recognised device identity that
 * differs from (or fills) the encounter's stored deviceId, the encounter is
 * silently updated — no banner, no dialog (signed-off behaviour).
 */
export function useSyncEncounterDeviceFromScan({
  encounterId,
  instances,
  encounterCollection,
  currentDeviceId,
}: {
  encounterId: string | null | undefined;
  instances: ReadonlyArray<ImagingInstance>;
  encounterCollection: RxCollection<EpocusEncounter> | null;
  currentDeviceId: string | null | undefined;
}): void {
  const { id: userId } = useUser();
  const { getCurrentTime } = useServerTime();

  useEffect(() => {
    if (!encounterId || !encounterCollection || instances.length === 0) return;

    const detected = matchDeviceFromInstances(instances);
    if (!detected || detected === currentDeviceId) return;

    let cancelled = false;
    (async () => {
      try {
        const doc = await encounterCollection.findOne(encounterId).exec();
        if (cancelled || !doc || doc.deviceId === detected) return;
        const now = getCurrentTime().toISOString();
        await doc.incrementalModify((data) => ({
          ...data,
          deviceId: detected,
          meta: { ...data.meta, updatedAt: now, updatedByUserId: userId },
        }));
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to sync encounter device from scan", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    encounterId,
    instances,
    encounterCollection,
    currentDeviceId,
    userId,
    getCurrentTime,
  ]);
}
